import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import { EditOutlined } from "@mui/icons-material";

import { NumericFormat } from "react-number-format";

import { forwardRef, useEffect, useState } from "react";

import { useFetcher } from "react-router-dom";

import { first, isObject } from "radash";

import PricingVariableCard from "./PricingVariableCard";

import { basePath } from "./../../../config/bootstrap.js";

const RegularTableCell = ({
  timeOnSite,
  value,
  valueFormatter = (v) => v,
  edit,
  state,
}) => {
  return (
    <TableCell
      sx={{ width: 100, ...(state === "changed" && { color: "info.main" }) }}
    >
      {!edit || state === "changed" ? valueFormatter(value, timeOnSite) : "-"}
    </TableCell>
  );
};

const EditableTableCell = forwardRef(
  ({ edit, value, state, onBaseRateChange, ...props }, ref) => {
    if (edit) {
      return (
        <TableCell sx={{ width: 100, p: 0 }}>
          <NumericFormat
            customInput={TextField}
            InputProps={{
              startAdornment: "$",
              sx: {
                borderRadius: 0,
                fontSize: "14px",
                ...(state === "changed" && { color: "info.main" }),
              },
            }}
            onChange={(e) => onBaseRateChange(e.target.value)}
            fullWidth
            {...(state === "error" && { error: true })}
            {...(state === "changed" && { color: "info", focused: true })}
            defaultValue={value}
          />
        </TableCell>
      );
    }

    return <RegularTableCell value={value} {...props} />;
  }
);

const mapTimeOnSites = (values, mapperFn) =>
  Object.keys(values)
    .sort()
    .map((key, idx) => mapperFn(key, values[key], idx));

const baseTimeOnSiteRateOf = (values) => {
  if (isObject(values)) {
    const timeOnSite = first(Object.keys(values).sort());

    return values[timeOnSite];
  }
};

const getResponseError = (response) => first(response?.errors?.base_rate || []);

const AudiencePricePerTimeOnSiteTable = ({
  data: dataFromProp,
  currentQuery,
  onUpdated,
  isEditing = false,
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [data, setData] = useState({ original: dataFromProp, ...dataFromProp });
  const [snackbar, setSnackbar] = useState(null);
  const validator = useFetcher();
  const submitter = useFetcher();

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleUpdate = (base_rate) => {
    const payload = { ...currentQuery, base_rate };

    submitter.submit(payload, {
      method: "post",
      action: `${basePath}/managed_plus/pricing/account_audience_floors/${currentQuery.account_id}`,
    });
  };

  useEffect(() => {
    setData({
      original: dataFromProp,
      ...dataFromProp,
      base_rate: baseTimeOnSiteRateOf(dataFromProp.audience_floors),
    });
  }, [dataFromProp]);

  useEffect(() => {
    if (validator.state === "idle" && validator.data) {
      if ("audience_floors" in validator.data) {
        setData(({ original, base_rate }) => ({
          original,
          base_rate,
          ...validator.data,
          enableConfirm: true,
          state: "changed",
        }));
      } else {
        setData((prev) => ({
          ...prev,
          data_revenue_share: 0,
          error: getResponseError(validator.data),
          state: "error",
        }));
      }
    }
  }, [validator]);

  useEffect(() => {
    if (!isEditing && showEdit) {
      setShowEdit(false);
    }
  }, [isEditing, showEdit]);

  useEffect(() => {
    if (submitter.state === "idle" && submitter.data) {
      setData(({ base_rate }) => ({
        original: submitter.data,
        base_rate,
        ...submitter.data,
      }));
      setSnackbar({ children: "UPDATED", severity: "success" });
      setShowEdit(false);
    }
  }, [submitter]);

  return (
    <Stack>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Toolbar
          sx={{
            bgcolor: ({ palette }) =>
              isEditing ? "#A3BFFD" : palette.action.disabledBackground,
          }}
          variant="dense"
          component={Paper}
          square
        >
          <Typography variant="body2" component="div">
            CPC Floors
          </Typography>
        </Toolbar>
        <Table>
          <TableHead>
            <TableRow key="header">
              {mapTimeOnSites(
                data.audience_floors,
                (timeOnSite, value, idx) => (
                  <RegularTableCell
                    key={timeOnSite}
                    value={`${timeOnSite}s`}
                    variant="head"
                  />
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key="values">
              {mapTimeOnSites(
                data.audience_floors,
                (timeOnSite, value, idx) => {
                  return idx === 0 ? (
                    <EditableTableCell
                      key={timeOnSite}
                      timeOnSite={timeOnSite}
                      value={value}
                      edit={showEdit}
                      state={data.state}
                      onBaseRateChange={(value) => {
                        setData(({ ...prev }) => ({
                          ...prev,
                          base_rate: value,
                          enableConfirm: false,
                          state: null,
                        }));
                      }}
                      valueFormatter={(v) =>
                        (value && `$${value}`) ?? <>&nbsp;</>
                      }
                      variant="body"
                    />
                  ) : (
                    <RegularTableCell
                      key={timeOnSite}
                      timeOnSite={timeOnSite}
                      edit={showEdit}
                      valueFormatter={(v) =>
                        (value && `$${value}`) ?? <>&nbsp;</>
                      }
                      value={value}
                      variant="body"
                      state={data.state}
                    />
                  );
                }
              )}
            </TableRow>
          </TableBody>
        </Table>
        <FormHelperText error>{data.error ?? " "}</FormHelperText>
        <Toolbar sx={{ p: 0 }} variant="regular" disableGutters>
          {isEditing &&
            (!showEdit ? (
              <Button
                variant="contained"
                onClick={(e) => setShowEdit((prev) => !prev)}
              >
                Update
              </Button>
            ) : (
              <Button
                variant="contained"
                type="submit"
                onClick={(e) => {
                  validator.submit(
                    { base_rate: data.base_rate, ...currentQuery },
                    {
                      action: `${basePath}/managed-plus/pricing/preview_floors_changes/account`,
                      method: "post",
                      formEncType: "application/json",
                    }
                  );
                }}
              >
                Recalculate
              </Button>
            ))}
        </Toolbar>
      </Box>
      <PricingVariableCard
        state={data.state}
        dataRevenueShare={data?.data_revenue_share?.toString()}
        showActionButtons={showEdit}
        disableConfirm={!data.enableConfirm || data.state === "error"}
        onConfirm={() => handleUpdate(data.base_rate)}
        onCancel={() => {
          setData(({ original }) => ({
            original, ...original,
            base_rate: baseTimeOnSiteRateOf(original.audience_floors)
          }));
          setSnackbar({
            children: "CANCELLED",
            severity: "error",
            sx: { bgcolor: ({ palette }) => palette.grey[700], color: "white" },
            color: "white",
          });
          setShowEdit(false);
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "right" }}>
        {showEdit && (
          <Alert variant="filled" icon={<EditOutlined />} color="info" sx={{ backgroundColor: "#558BF7" }}>
            EDIT MODE
          </Alert>
        )}
      </Box>
      {!!snackbar && (
        <Snackbar
          open
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={6000}
        >
          <Alert variant="filled" {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </Stack>
  );
};

export default AudiencePricePerTimeOnSiteTable;
